import { useMutation } from "@apollo/client/react";
import {
  GetConsentStatus,
  UpdateConsent,
  UpdateConsentVariables,
} from "__generated__/types";
import { useAuth } from "common/auth/Auth";
import { Button } from "common/buttons";
import { Box } from "common/components/Box";
import { ExpandableItem } from "common/components/ExpandableItem";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { ListContainer } from "common/components/lists/ListContainer";
import { ListItem } from "common/components/lists/ListItem";
import { Loading } from "common/components/Loading";
import { NoDataError } from "common/components/NoDataError";
import { Section } from "common/components/Section";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import gql from "graphql-tag";
import React from "react";

const MUTATION = gql`
  mutation UpdateConsent($input: UpdateUserConsentInput!) {
    updateUserConsents(input: $input) {
      user {
        id
      }
    }
  }
`;

export const consentStatusFragment = gql`
  fragment ConsentStatusFragment on UserConsent {
    isGiven
    completed
    basis {
      id
      title
      type
      purpose
      validFrom
    }
  }
`;

const GET_CONSENT_STATUS = gql`
  query GetConsentStatus {
    consentStatusList {
      ...ConsentStatusFragment
    }
  }
  ${consentStatusFragment}
`;

interface Props {
  includeCompletedConsents?: boolean;
}

export const ConsentForm: React.FC<React.PropsWithChildren<Props>> = ({
  includeCompletedConsents = true,
}) => {
  const { data, loadingOrError, refetch } =
    useSimpleQuery<GetConsentStatus>(GET_CONSENT_STATUS);

  const auth = useAuth();

  const [mutate, { error, loading }] = useMutation<
    UpdateConsent,
    UpdateConsentVariables
  >(MUTATION);

  const onConsentClick = async (id: string, isGiven: boolean) => {
    const result = await mutate({
      variables: {
        input: {
          consentId: id,
          isGiven,
        },
      },
    });

    if (result.data) {
      await refetch();

      auth.refetchAuthData();
    }
  };

  if (loadingOrError) {
    return <Loading />;
  }

  if (!data || !data.consentStatusList) {
    return <NoDataError />;
  }

  const consents = data.consentStatusList.filter((consent) =>
    includeCompletedConsents ? true : !consent.completed,
  );

  if (!consents.length && !includeCompletedConsents) {
    return null;
  }

  return (
    <Section title="Samtykker">
      <ListContainer>
        {data.consentStatusList
          .filter((consent) =>
            includeCompletedConsents ? true : !consent.completed,
          )
          .map((consent) => {
            return (
              <ListItem key={consent.basis.type}>
                <ExpandableItem
                  title={consent.basis.title}
                  subText={
                    consent.completed
                      ? consent.isGiven
                        ? "Samtykke gitt"
                        : "Samtykke avslått"
                      : "Mangler svar"
                  }
                  content={
                    <Box rounded={false} centeredContent={false}>
                      <div>
                        <p>{consent.basis.purpose}</p>
                      </div>
                      <Button
                        onClick={() => onConsentClick(consent.basis.id, true)}
                      >
                        Jeg samtykker
                      </Button>
                      <Button
                        onClick={() => onConsentClick(consent.basis.id, false)}
                      >
                        Jeg samtykker ikke
                      </Button>
                      <MutationLoadingOrError loading={loading} error={error} />
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
      </ListContainer>
    </Section>
  );
};
